import { createContext, useContext, useReducer, type ReactNode } from 'react';
import type {
  AppState,
  AppAction,
  FlowType,
  UserType,
  QuizAnswers,
  ApiRequest,
  ApiStatus,
  ApiResponse,
  MapData,
} from '../types';

const initialState: AppState = {
  flow: null,
  quizAnswers: {},
  expressInput: {
    text: '',
    userType: null,
    incomeRange: null,
  },
  auth: {
    email: '',
    phone: '',
  },
  api: {
    status: 'idle',
    request: null,
    result: null,
    error: null,
    incomeBracket: null,
  },
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_FLOW':
      return { ...state, flow: action.payload };

    case 'SET_USER_TYPE':
      return {
        ...state,
        quizAnswers: {
          ...state.quizAnswers,
          userType: action.payload,
        },
      };

    case 'UPDATE_QUIZ_ANSWERS':
      return {
        ...state,
        quizAnswers: {
          ...state.quizAnswers,
          ...action.payload,
        },
      };

    case 'RESET_QUIZ_ANSWERS':
      return {
        ...state,
        quizAnswers: {},
      };

    case 'SET_EXPRESS_TEXT':
      return {
        ...state,
        expressInput: {
          ...state.expressInput,
          text: action.payload,
        },
      };

    case 'SET_EXPRESS_USER_TYPE':
      return {
        ...state,
        expressInput: {
          ...state.expressInput,
          userType: action.payload,
        },
      };

    case 'SET_EXPRESS_INCOME_RANGE':
      return {
        ...state,
        expressInput: {
          ...state.expressInput,
          incomeRange: action.payload,
        },
      };

    case 'RESET_EXPRESS_INPUT':
      return {
        ...state,
        expressInput: {
          text: '',
          userType: null,
          incomeRange: null,
        },
      };

    case 'SET_AUTH_EMAIL':
      return {
        ...state,
        auth: {
          ...state.auth,
          email: action.payload,
        },
      };

    case 'SET_AUTH_PHONE':
      return {
        ...state,
        auth: {
          ...state.auth,
          phone: action.payload,
        },
      };

    case 'RESET_AUTH':
      return {
        ...state,
        auth: {
          email: '',
          phone: '',
        },
      };

    case 'SET_API_REQUEST':
      return {
        ...state,
        api: {
          ...state.api,
          request: action.payload,
        },
      };

    case 'SET_API_STATUS':
      return {
        ...state,
        api: {
          ...state.api,
          status: action.payload,
        },
      };

    case 'SET_API_RESULT':
      return {
        ...state,
        api: {
          ...state.api,
          status: 'success',
          result: action.payload,
          error: null,
        },
      };

    case 'SET_API_ERROR':
      return {
        ...state,
        api: {
          ...state.api,
          status: 'error',
          error: action.payload,
        },
      };

    case 'RESET_API':
      return {
        ...state,
        api: {
          status: 'idle',
          request: null,
          result: null,
          error: null,
          incomeBracket: null,
        },
      };

    case 'UPDATE_MAP_DATA':
      return {
        ...state,
        api: {
          ...state.api,
          result: state.api.result
            ? { ...state.api.result, map: action.payload }
            : null,
        },
      };

    case 'SET_PLAYGROUND_ID':
      return {
        ...state,
        api: {
          ...state.api,
          result: state.api.result
            ? { ...state.api.result, id: action.payload }
            : null,
        },
      };

    case 'SET_INCOME_BRACKET':
      return {
        ...state,
        api: {
          ...state.api,
          incomeBracket: action.payload,
        },
      };

    default:
      return state;
  }
}

interface AppContextValue {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Helper actions
  setFlow: (flow: FlowType) => void;
  setUserType: (userType: UserType) => void;
  updateQuizAnswers: (answers: Partial<QuizAnswers>) => void;
  resetQuizAnswers: () => void;
  setExpressText: (text: string) => void;
  setExpressUserType: (userType: UserType | null) => void;
  setExpressIncomeRange: (range: string | null) => void;
  resetExpressInput: () => void;
  setAuthEmail: (email: string) => void;
  setAuthPhone: (phone: string) => void;
  resetAuth: () => void;
  setApiRequest: (request: ApiRequest) => void;
  setApiStatus: (status: ApiStatus) => void;
  setApiResult: (result: ApiResponse) => void;
  setApiError: (error: string) => void;
  resetApi: () => void;
  updateMapData: (map: MapData) => void;
  setPlaygroundId: (id: string) => void;
  setIncomeBracket: (bracket: string) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const value: AppContextValue = {
    state,
    dispatch,
    setFlow: (flow) => dispatch({ type: 'SET_FLOW', payload: flow }),
    setUserType: (userType) => dispatch({ type: 'SET_USER_TYPE', payload: userType }),
    updateQuizAnswers: (answers) => dispatch({ type: 'UPDATE_QUIZ_ANSWERS', payload: answers }),
    resetQuizAnswers: () => dispatch({ type: 'RESET_QUIZ_ANSWERS' }),
    setExpressText: (text) => dispatch({ type: 'SET_EXPRESS_TEXT', payload: text }),
    setExpressUserType: (userType) => dispatch({ type: 'SET_EXPRESS_USER_TYPE', payload: userType }),
    setExpressIncomeRange: (range) => dispatch({ type: 'SET_EXPRESS_INCOME_RANGE', payload: range }),
    resetExpressInput: () => dispatch({ type: 'RESET_EXPRESS_INPUT' }),
    setAuthEmail: (email) => dispatch({ type: 'SET_AUTH_EMAIL', payload: email }),
    setAuthPhone: (phone) => dispatch({ type: 'SET_AUTH_PHONE', payload: phone }),
    resetAuth: () => dispatch({ type: 'RESET_AUTH' }),
    setApiRequest: (request) => dispatch({ type: 'SET_API_REQUEST', payload: request }),
    setApiStatus: (status) => dispatch({ type: 'SET_API_STATUS', payload: status }),
    setApiResult: (result) => dispatch({ type: 'SET_API_RESULT', payload: result }),
    setApiError: (error) => dispatch({ type: 'SET_API_ERROR', payload: error }),
    resetApi: () => dispatch({ type: 'RESET_API' }),
    updateMapData: (map) => dispatch({ type: 'UPDATE_MAP_DATA', payload: map }),
    setPlaygroundId: (id) => dispatch({ type: 'SET_PLAYGROUND_ID', payload: id }),
    setIncomeBracket: (bracket) => dispatch({ type: 'SET_INCOME_BRACKET', payload: bracket }),
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
